<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1100" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>
      <v-card-text>
        <div class="mb-4">
          <label class="label-input">Section Name</label>
          <v-text-field v-model="fields.title" hide-details />
        </div>

        <!-- Layout -->
        <SectionLayout :settings="fields.settings" :container-page="fields.containerPage" />

        <!-- Title Card -->
        <div class="mb-5">
          <label class="label-input">Title Section</label>
          <div class="group-item">
            <v-checkbox v-model="fields.titleCard.show" label="Show title block" hide-details class="mt-0 mb-2" />
            <ckeditor
              v-if="fields.titleCard.show"
              :editor="editor"
              v-model="fields.titleCard.content"
              :config="editorConfig"
            />
          </div>
        </div>

        <!-- Slider settings -->
        <div class="mb-5">
          <label class="label-input">Slider Settings</label>
          <div class="group-item">
            <v-row>
              <v-col cols="12" md="4">
                <label>Columns Per View</label>
                <v-text-field
                  type="number"
                  min="1"
                  max="6"
                  v-model.number="fields.list.colPerView"
                  hide-details
                  class="mt-0"
                />
              </v-col>
              <v-col cols="12" md="4">
                <label>Space Between (px)</label>
                <v-text-field
                  type="number"
                  min="0"
                  v-model.number="fields.list.spaceBetween"
                  hide-details
                  class="mt-0"
                />
              </v-col>
              <v-col cols="12" md="4">
                <ColorPicker
                  label="Star Color"
                  :value="fields.list.colorStar"
                  name="reviews-color-star"
                  default-color="#FFD700"
                  @input="value => (fields.list.colorStar = value)"
                />
              </v-col>
            </v-row>
          </div>
        </div>

        <!-- Review Items -->
        <div class="mb-5">
          <div class="d-flex justify-space-between align-center mb-2">
            <label class="label-input mb-0">Review Items</label>
            <v-btn small color="primary" @click="handleAddItem">
              <v-icon size="16">mdi-plus</v-icon>
              <span>Add Review</span>
            </v-btn>
          </div>
          <div class="group-item">
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(item, idx) in fields.list.items" :key="`review-${idx}`">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center" style="width: 100%">
                    <span>{{ item.reviewer || `Review ${idx + 1}` }} - {{ item.stars }}★</span>
                    <v-btn icon @click.stop="handleRemoveItem(idx)">
                      <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="sub-block">
                    <label>Image</label>
                    <UploadImage
                      :image="{ imageSrc: item.image }"
                      :height="`220`"
                      :typeUpload="'image'"
                      @callback="obj => (item.image = obj.imageSrc)"
                      @callbackDelete="item.image = ''"
                    />
                  </div>

                  <div class="sub-block">
                    <label>Stars (0 - 5)</label>
                    <!-- <v-text-field type="number" min="0" max="5" v-model.number="item.stars" hide-details class="mt-0" /> -->
                    <v-rating
                      color="warning"
                      background-color="#c1c1c1"
                      empty-icon="mdi-star-outline"
                      full-icon="mdi-star"
                      hover
                      length="5"
                      size="28"
                      :value="item.stars"
                      @input="value => (item.stars = Number(value))"
                    />
                  </div>

                  <div class="sub-block">
                    <label>Comment</label>
                    <ckeditor :editor="editor" v-model="item.comment" :config="editorConfig" />
                  </div>

                  <div class="sub-block">
                    <label>Reviewer</label>
                    <v-text-field v-model="item.reviewer" hide-details class="mt-0" />
                  </div>

                  <div class="sub-block">
                    <v-checkbox
                      v-model="item.showVerified"
                      label="Show 'Verified Buyer' badge"
                      hide-details
                      class="mt-0"
                    />
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>

        <!-- Btn Primary -->
        <div class="mb-5">
          <label class="label-input">Primary Button</label>
          <div class="group-item">
            <ButtonPrimary :button="fields.buttonOutSide" />
          </div>
        </div>
      </v-card-text>
      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { cloneDeep } from 'lodash';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import { updateHtmlFromData } from '../common/html/update';
import UploadImage from '../common/form/upload-file/upload-image.vue';
import ButtonPrimary from '../common/ui-modules/button-primary/editor.vue';
import SectionLayout from '../common/ui-modules/section-layout/editor.vue';
import ColorPicker from '../common/ui-modules/inputs/color-picker.vue';

const buildButtonAreaDefault = () => ({
  align: 'center',
  backgroundColor: '#606F99',
  color: '#FFFFFF',
  radius: 4,
  iconPrefix: {
    show: false,
    src: '',
  },
  iconSuffix: {
    show: false,
    src: '',
  },
  text: 'TRY IT TODAY, RISK-FREE !',
  link: '#',
  show: true,
  subBtn: {
    show: false,
    text: '',
  },
  policy: {
    show: false,
    colPerView: 2,
    alignContent: 'center',
    items: [],
  },
});

const buildReviewItemDefault = () => ({
  image: '',
  stars: 5,
  comment: '<h3>"Great product."</h3><p>"Highly recommended."</p>',
  reviewer: 'Customer',
  showVerified: true,
});

const getDefaultFields = () => ({
  title: 'Reviews Boxer Section',
  settings: {
    paddingTop: true,
    paddingBottom: true,
    backgroundColor: '#FFFFFF',
    textColor: '#000000',
    overlayColor: '#000000',
    overlayOpacity: 0,
    showOverlay: false,
    backgroundImage: '',
    useCustomWidth: false,
    customWidth: 1204,
  },
  containerPage: {
    enableStyle: false,
    background: '#FFFFFF',
  },
  titleCard: {
    show: true,
    content: '<h2>125K +</h2><h2>Happy Customers</h2>',
  },
  list: {
    colPerView: 4,
    spaceBetween: 16,
    colorStar: '#FFD700',
    items: [buildReviewItemDefault()],
  },
  buttonOutSide: buildButtonAreaDefault(),
});

export default {
  name: 'ReviewsBoxerSectionEditor',
  components: { UploadImage, ButtonPrimary, SectionLayout, ColorPicker },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: getDefaultFields(),
    };
  },
  mounted() {
    const def = getDefaultFields();
    const extract = cloneDeep(this.sectionEditor.sectionExtract || {});

    this.fields = {
      ...def,
      ...extract,
      list: {
        ...def.list,
        ...(extract.list || {}),
        items: extract.list && extract.list.items && extract.list.items.length ? extract.list.items : def.list.items,
      },
    };
  },
  methods: {
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const updateHtml = updateHtmlFromData.sectionCardReviewsBoxer(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
    handleAddItem() {
      this.fields.list.items.push(cloneDeep(buildReviewItemDefault()));
    },
    handleRemoveItem(idx) {
      if (this.fields.list.items.length <= 1) return;
      this.fields.list.items.splice(idx, 1);
    },
  },
};
</script>

<style lang="scss" scoped>
.group-item {
  border: 1px solid #0000000f;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}
.sub-block {
  border-top: 1px dashed #0000001a;
  padding-top: 12px;
  margin-top: 12px;
}
.ck-editor__editable_inline,
.ck-editor__editable {
  min-height: 120px;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
</style>
